import { ServiceResult } from "./api";

// Recipe-related type definitions

export enum MealType {
  MEAL = "meal",
  BREAKFAST = "breakfast",
  LUNCH = "lunch",
  SNACKS = "snack",
  DINNER = "dinner",
  DRINKS = "drinks",
  DESSERTS = "desserts",
}

// Recipe components
export enum RecipeComponentType {
  Ingredient = "ingredient",
  UserProduct = "user_product",
}

export type MeasurementUnitType = "mass" | "volume" | "household";

// One entry of a food's `measurement_units` list. The label is the unit's
// identity; `grams_per_unit` is the multiplier a quantity is converted with.
export interface MeasurementUnit {
  id: number | null;
  is_custom: boolean;
  type: MeasurementUnitType;
  label: string;
  amount: number;
  grams: number;
  grams_per_unit: number;
}

// Global ingredient
export interface IngredientEntity {
  id: number;
  name: string;
  calories_kcal_per_100g: number;
  protein_g_per_100g: number;
  carbs_g_per_100g: number;
  fat_g_per_100g: number;
  fiber_g_per_100g?: number | null;
  sodium_mg_per_100g?: number | null;
  default_serving_amount?: number | null;
  default_serving_unit?: string | null;
  default_serving_grams?: number | null;
  default_serving_description?: string | null;
  measurement_units: MeasurementUnit[];
}

export interface RecipeComponent {
  id: number;
  recipe_id: number;
  ingredient_id: number | null;
  user_product_id: number | null;
  portion_grams: number;
  quantity: number | null;
  measurement_unit_label: string | null;
  position: number;
  section: string | null;
  component_type: RecipeComponentType | null;
  ingredient?: IngredientEntity | null;
  created_at?: string;
  updated_at?: string;
}

// The amount of a component: grams always, plus the unit pair when one was
// chosen (`quantity` and `measurement_unit_label` are both set or both null).
export interface ComponentAmount {
  portion_grams: number;
  quantity: number | null;
  measurement_unit_label: string | null;
}

// What the API accepts for a component's amount: plain grams, or a quantity
// in one of the food's units. Never both.
export type ComponentAmountPayload =
  | { portion_grams: number }
  | { quantity: number; measurement_unit_label: string };

// Save payload
export type CreateRecipeComponentDTO = (
  | { ingredient_id: number }
  | { user_product_id: number }
) &
  ComponentAmountPayload & {
    position: number;
    section: string | null;
  };

export interface RecipeComponentFormValue extends ComponentAmount {
  key: string;
  food: IngredientEntity;
  sectionId: string | null;
}

export interface RecipeSectionFormValue {
  id: string;
  name: string;
}

export interface RecipeImage {
  id: number;
  original_name: string;
  storage_name: string;
  path: string;
  fileable_type: string;
  fileable_id: number;
  type: string;
  full_path: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Recipe {
  id: number;
  name: string;
  calories: number;
  default_num_of_servings: number;
  description?: string;
  protein?: number;
  carbs?: number;
  fat?: number;
  fiber?: number;
  sodium_mg?: number;
  components?: RecipeComponent[];
  directions?: string;
  meal_type: MealType;
  note?: string;
  recipe_image?: RecipeImage | null;
  created_at: string;
  updated_at: string;
}

export interface RecipeResponse {
  data: Recipe[];
  total: number;
  current_page: number;
  last_page: number;
  per_page: number;
  from: number;
  to: number;
}

export interface RecipePaginatedResponse {
  data: RecipeResponse;
}

export interface RecipeDetailResponse {
  data: Recipe;
}

// Nutrition is computed by the API from the components, so no macro fields
// travel in the payload.
export interface CreateRecipeInput {
  name: string;
  default_num_of_servings: number;
  description?: string | null;
  components: CreateRecipeComponentDTO[];
  directions?: string | null;
  meal_type: MealType;
  note?: string | null;
}

export interface UpdateRecipeInput {
  name?: string;
  default_num_of_servings?: number;
  description?: string | null;
  components?: CreateRecipeComponentDTO[];
  directions?: string | null;
  meal_type?: MealType;
  note?: string | null;
}

export interface RecipeFilters {
  search?: string;
  min_calories?: number;
  max_calories?: number;
  min_protein?: number;
  max_protein?: number;
}

export type RecipeServiceResponse<T> = ServiceResult<T>;
